import {ref, computed, nextTick, onMounted, onBeforeUnmount, watch, unref} from 'vue'
import interact from 'interactjs'
import {useThemeStore} from '@js/store/ThemeStore.js'

export function useSwipePanel(options = {}) {
    const {
        isOpen,
        panelRef,
        handleRef = null,
        onClose = () => {},
        direction = 'bottom',
        closeThreshold = 110,
        openDuration = 340,
        closeDuration = 320,
        fadeDivisor = 260,
        lockScroll = true,
        enabled = true,
    } = options

    const themeStore = useThemeStore()

    const rendered = ref(false)
    const visible = ref(false)
    const dragging = ref(false)

    const dragX = ref(0)
    const dragY = ref(0)

    const isClosing = ref(false)
    const closedByPanel = ref(false)

    let interactable = null
    let openTimer = null
    let closeTimer = null
    let scrollLockedByMe = false

    const isEnabled = computed(() => unref(enabled))

    const clearTimers = () => {
        if (openTimer) {
            clearTimeout(openTimer)
            openTimer = null
        }

        if (closeTimer) {
            clearTimeout(closeTimer)
            closeTimer = null
        }
    }

    const lockBodyScroll = () => {
        if (!lockScroll || scrollLockedByMe) return
        themeStore.lockBodyOverflow()
        scrollLockedByMe = true
    }

    const unlockBodyScroll = () => {
        if (!lockScroll || !scrollLockedByMe) return
        themeStore.unlockBodyOverflow()
        scrollLockedByMe = false
    }

    const resetDrag = () => {
        dragging.value = false
        dragX.value = 0
        dragY.value = 0
    }

    const getPanelEl = () => unref(panelRef)
    const getHandleEl = () => unref(handleRef)

    const destroyInteract = () => {
        if (interactable) {
            interactable.unset()
            interactable = null
        }
    }

    const backdropStyle = computed(() => {
        if (!isEnabled.value) {
            return {
                opacity: visible.value ? 1 : 0,
                transition: 'opacity 200ms ease',
            }
        }

        let offset = 0

        if (direction === 'bottom') {
            offset = dragY.value
        } else if (direction === 'left' || direction === 'right') {
            offset = Math.abs(dragX.value)
        }

        const opacity = visible.value ? Math.max(0, 1 - offset / fadeDivisor) : 0

        return {
            opacity,
            transition: dragging.value ? 'none' : 'opacity 260ms ease',
        }
    })

    const panelStyle = computed(() => {
        if (!isEnabled.value) {
            return {}
        }

        if (direction === 'bottom') {
            const hiddenPercent = visible.value ? 0 : 100

            return {
                transform: `translate3d(0, calc(${hiddenPercent}% + ${dragY.value}px), 0)`,
                transition: dragging.value
                    ? 'none'
                    : `transform ${visible.value ? openDuration : closeDuration}ms cubic-bezier(0.22, 1, 0.36, 1)`,
                willChange: 'transform',
            }
        }

        if (direction === 'left') {
            const hiddenPercent = visible.value ? 0 : -100

            return {
                transform: `translate3d(calc(${hiddenPercent}% + ${dragX.value}px), 0, 0)`,
                transition: dragging.value
                    ? 'none'
                    : `transform ${visible.value ? openDuration : closeDuration}ms cubic-bezier(0.22, 1, 0.36, 1)`,
                willChange: 'transform',
                touchAction: 'none',
            }
        }

        if (direction === 'right') {
            const hiddenPercent = visible.value ? 0 : 100

            return {
                transform: `translate3d(calc(${hiddenPercent}% + ${dragX.value}px), 0, 0)`,
                transition: dragging.value
                    ? 'none'
                    : `transform ${visible.value ? openDuration : closeDuration}ms cubic-bezier(0.22, 1, 0.36, 1)`,
                willChange: 'transform',
                touchAction: 'none',
            }
        }

        return {}
    })

    const initInteract = async () => {
        if (!isEnabled.value) return

        await nextTick()

        const panelEl = getPanelEl()
        const handleEl = getHandleEl()

        if (!panelEl) return

        destroyInteract()

        interactable = interact(panelEl).draggable({
            allowFrom: handleEl || panelEl,
            ignoreFrom: 'input, textarea, button, select, option, a, label',
            inertia: false,
            listeners: {
                start() {
                    dragging.value = true
                },
                move(event) {
                    if (direction === 'bottom') {
                        dragY.value = Math.max(0, dragY.value + event.dy)
                    }

                    if (direction === 'left') {
                        dragX.value = Math.min(0, dragX.value + event.dx)
                    }

                    if (direction === 'right') {
                        dragX.value = Math.max(0, dragX.value + event.dx)
                    }
                },
                end() {
                    dragging.value = false

                    if (direction === 'bottom' && dragY.value >= closeThreshold) {
                        close()
                        return
                    }

                    if ((direction === 'left' || direction === 'right') && Math.abs(dragX.value) >= closeThreshold) {
                        close()
                        return
                    }

                    resetDrag()
                },
            },
        })
    }

    const finalizeClose = (runOnClose = true) => {
        clearTimers()
        rendered.value = false
        visible.value = false
        isClosing.value = false
        destroyInteract()
        resetDrag()
        unlockBodyScroll()

        if (runOnClose) {
            closedByPanel.value = true
            onClose()
            queueMicrotask(() => {
                closedByPanel.value = false
            })
        }
    }

    const startCloseAnimation = (runOnClose = true) => {
        if (!isEnabled.value) {
            finalizeClose(runOnClose)
            return
        }

        if (isClosing.value) return

        clearTimers()
        isClosing.value = true
        dragging.value = false
        resetDrag()
        visible.value = false

        closeTimer = setTimeout(() => {
            finalizeClose(runOnClose)
        }, closeDuration)
    }

    const open = async () => {
        clearTimers()

        rendered.value = true
        visible.value = false
        isClosing.value = false
        resetDrag()
        lockBodyScroll()

        if (!isEnabled.value) {
            await nextTick()
            visible.value = true
            return
        }

        await nextTick()
        await initInteract()

        openTimer = setTimeout(() => {
            visible.value = true
        }, 16)
    }

    const close = () => {
        startCloseAnimation(true)
    }

    const syncClosedState = () => {
        clearTimers()
        destroyInteract()
        rendered.value = false
        visible.value = false
        isClosing.value = false
        resetDrag()
        unlockBodyScroll()
    }

    const handleBackgroundClick = (event) => {
        const panelEl = getPanelEl()
        if (!panelEl) return

        if (!panelEl.contains(event.target)) {
            close()
        }
    }

    watch(
        [() => unref(isOpen), () => isEnabled.value],
        async ([openValue]) => {
            if (openValue) {
                if (!rendered.value) {
                    await open()
                } else if (!isEnabled.value) {
                    destroyInteract()
                    resetDrag()
                    visible.value = true
                } else {
                    await initInteract()
                }
                return
            }

            if (closedByPanel.value) {
                return
            }

            if (rendered.value || visible.value) {
                startCloseAnimation(false)
            } else {
                syncClosedState()
            }
        }
    )

    onMounted(async () => {
        if (unref(isOpen)) {
            await open()
        }
    })

    onBeforeUnmount(() => {
        clearTimers()
        destroyInteract()
        unlockBodyScroll()
    })

    return {
        rendered,
        visible,
        dragging,
        dragX,
        dragY,
        panelStyle,
        backdropStyle,
        open,
        close,
        resetDrag,
        handleBackgroundClick,
    }
}
